export interface HighlightDayCell {
  date: string;
  trendingTopic?: string;
  trendingName?: string;
  postsCount?: number;
  secondaryHighlights?: Array<{ topic: string; name?: string }>;
}

export type MonthExamplePost = {
  id: string;
  url: string;
  title?: string;
  text?: string;
  publishedAt?: string;
  imageUrls?: string[];
};

export type MonthHighlightItem = {
  topic: string;
  name?: string;
  examplePosts?: MonthExamplePost[];
};

export interface MonthHighlights {
  trendingTopic?: string;
  trendingName?: string;
  examplePosts?: MonthExamplePost[];
  secondaryHighlights?: MonthHighlightItem[];
}

const TRAILING_INCOMPLETE =
  /\b(the|a|an|of|and|or|to|for|in|on|at|by|with|from|as|that|throughout|who|which|whom|whose|people of|for the|in the|of the|and the|and who|who is|from the|throughout the)\s*$/i;

const HISTORICAL_NAMES = new Set([
  'thomas jefferson',
  'george washington',
  'abraham lincoln',
  'benjamin franklin',
  'alexander hamilton',
  'john adams',
  'james madison',
  'teddy roosevelt',
  'franklin roosevelt'
]);

const GENERIC_TOPIC =
  /^(economic growth|political commentary|endorsements and support|international relations|legal proceedings|general news|controversies)\b/i;

export function acceptModelHeadline(topic: string | undefined): string | undefined {
  const t = (topic || '').replace(/\s+/g, ' ').trim();
  if (!t) return undefined;
  if (/^https?:/i.test(t) || /^RT:?\s/i.test(t)) return undefined;
  if (/\/statuses\//i.test(t) || /truthsocial\.com/i.test(t)) return undefined;
  if (GENERIC_TOPIC.test(t)) return undefined;
  return t;
}

export function isCompleteHeadline(topic: string | undefined): boolean {
  if (!topic) return false;
  const t = topic.replace(/\s+/g, ' ').trim();
  if (t.length < 12 || t.length > 140) return false;
  if (/^[A-Z0-9 .,'’:-]+$/.test(t) && t === t.toUpperCase() && t.length < 40) return false;
  if (TRAILING_INCOMPLETE.test(t)) return false;
  if (/\b(u\.s\.a|u\.s|lt|st|mr|dr|gen|gov|sen)\.?$/i.test(t)) return false;
  if (/[,:;]$/.test(t)) return false;
  if ((t.match(/["“”]/g) || []).length % 2 === 1) return false;
  if (/\bI$/.test(t)) return false;
  if (/\b(represent|including)\s*$/i.test(t)) return false;
  if (/\b(where|who|whom|whose|which|that|and|or|for|with|from|under|of|to|our|my)\s+[A-Z][A-Za-z'’]*$/.test(t)) return false;
  if (/^https?:/i.test(t) || /^RT:?\s/i.test(t)) return false;
  if (/\/statuses\//i.test(t) || /truthsocial\.com/i.test(t)) return false;
  return true;
}

export function nameMentionsTopic(name: string | undefined, topic: string | undefined): boolean {
  if (!name || !topic) return false;
  const hay = topic.toLowerCase();
  if (hay.includes(name.toLowerCase())) return true;
  const parts = name.split(/\s+/).filter(Boolean);
  return parts.some((part) => part.length > 3 && hay.includes(part.toLowerCase()));
}

function utcToday(now: Date | string): string {
  if (typeof now === 'string') return now.slice(0, 10);
  return now.toISOString().slice(0, 10);
}

function usableName(name: string | undefined): name is string {
  if (!name) return false;
  return !HISTORICAL_NAMES.has(name.toLowerCase());
}

function openingKey(text: string): string {
  const tokens = text
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  return tokens.slice(0, 6).join(' ');
}

function clusterHeadline(members: ThemeMember[]): string {
  return [...members].sort((a, b) => (b.posts - a.posts) || (b.headline.length - a.headline.length))[0].headline;
}

interface ThemeMember {
  headline: string;
  name?: string;
  posts: number;
}

export function monthSoFarDays<T extends { date: string }>(days: T[], month: string, now: Date | string): T[] {
  const today = utcToday(now);
  if (today.startsWith(month)) return days.filter((d) => d.date <= today);
  return days;
}

export function rankMonthThemes(days: HighlightDayCell[]): Array<{
  topic: string;
  name?: string;
  count: number;
  posts: number;
}> {
  const clusters = new Map<string, ThemeMember[]>();
  const push = (headline: string | undefined, name: string | undefined, posts: number) => {
    const topic = acceptModelHeadline(headline);
    if (!topic) return;
    const key = openingKey(topic);
    if (!key) return;
    const member: ThemeMember = {
      headline: topic,
      ...(usableName(name) ? { name } : {}),
      posts
    };
    const prev = clusters.get(key);
    if (prev) prev.push(member);
    else clusters.set(key, [member]);
  };

  for (const day of days) {
    const posts = day.postsCount || 0;
    push(day.trendingTopic, day.trendingName, posts);
    for (const extra of day.secondaryHighlights || []) {
      push(extra.topic, extra.name, 0);
    }
  }

  const ranked = Array.from(clusters.values()).map((members) => {
    const clustered = members.length >= 2;
    const topic = clustered ? clusterHeadline(members) : members[0].headline;
    const names = members.map((m) => m.name).filter((name): name is string => !!name);
    const unanimous = names.length > 0 && names.every((name) => name.toLowerCase() === names[0].toLowerCase());
    const candidate = clustered
      ? (unanimous && names.length === members.length ? names[0] : undefined)
      : names[0];
    const name = nameMentionsTopic(candidate, topic) ? candidate : undefined;
    const posts = members.reduce((sum, m) => sum + m.posts, 0);
    return {
      topic,
      count: members.length,
      posts,
      ...(name ? { name } : {})
    };
  }).filter((theme) => acceptModelHeadline(theme.topic));
  ranked.sort((a, b) => (b.count - a.count) || (b.posts - a.posts));
  return ranked;
}

export function monthSoFarHighlights(
  days: HighlightDayCell[],
  month: string,
  now: Date | string
): MonthHighlights {
  const ranked = rankMonthThemes(monthSoFarDays(days, month, now));
  const best = ranked[0];
  const secondaryHighlights = ranked
    .slice(1)
    .filter((theme) => theme.topic.toLowerCase() !== (best?.topic || '').toLowerCase())
    .slice(0, 3)
    .map((theme) => ({ topic: theme.topic, ...(theme.name ? { name: theme.name } : {}) }));
  return {
    ...(best ? { trendingTopic: best.topic } : {}),
    ...(best?.name ? { trendingName: best.name } : {}),
    ...(secondaryHighlights.length ? { secondaryHighlights } : {})
  };
}

export function sanitizeMonthExamplePosts(value: unknown): MonthExamplePost[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: MonthExamplePost[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const raw = item as Partial<MonthExamplePost>;
    const id = typeof raw.id === 'string' ? raw.id.trim() : '';
    const url = typeof raw.url === 'string' ? raw.url.trim() : '';
    const key = id || url;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const imageUrls = Array.isArray(raw.imageUrls)
      ? raw.imageUrls.filter((imageUrl) => typeof imageUrl === 'string' && /^https:\/\//i.test(imageUrl))
      : [];
    out.push({
      id: id || url,
      url: url || id,
      ...(typeof raw.title === 'string' && raw.title ? { title: raw.title } : {}),
      ...(typeof raw.text === 'string' && raw.text ? { text: raw.text } : {}),
      ...(typeof raw.publishedAt === 'string' && raw.publishedAt ? { publishedAt: raw.publishedAt } : {}),
      ...(imageUrls.length ? { imageUrls } : {})
    });
    if (out.length >= 8) break;
  }
  return out;
}

function withExamplePosts(
  source: MonthHighlightItem | MonthHighlights | undefined,
  dest: { topic: string; name?: string }
): MonthHighlightItem {
  const examplePosts = sanitizeMonthExamplePosts(source?.examplePosts);
  return examplePosts.length ? { ...dest, examplePosts } : dest;
}

function cleanHighlight(item: MonthHighlightItem | undefined, primary?: string): MonthHighlightItem | undefined {
  const topic = acceptModelHeadline(item?.topic);
  if (!topic) return undefined;
  if (primary && topic.toLowerCase() === primary.toLowerCase()) return undefined;
  const name = usableName(item?.name) ? item!.name : undefined;
  return withExamplePosts(item, name ? { topic, name } : { topic });
}

/** Prefer a stored OpenAI month classification; otherwise use the local volume cluster. */
export function resolveMonthHighlights(
  stored: MonthHighlights | undefined,
  fallback: MonthHighlights
): MonthHighlights {
  const topic = acceptModelHeadline(stored?.trendingTopic);
  if (!topic) return fallback;
  const name = usableName(stored?.trendingName) && nameMentionsTopic(stored.trendingName, topic)
    ? stored!.trendingName
    : undefined;
  const secondaryHighlights = (stored?.secondaryHighlights || [])
    .map((item) => cleanHighlight(item, topic))
    .filter((item): item is MonthHighlightItem => !!item)
    .slice(0, 3);
  const examplePosts = sanitizeMonthExamplePosts(stored?.examplePosts);
  return {
    trendingTopic: topic,
    ...(name ? { trendingName: name } : {}),
    ...(examplePosts.length ? { examplePosts } : {}),
    ...(secondaryHighlights.length ? { secondaryHighlights } : {})
  };
}
